/**
 * /venues/:venueSlug/tournaments/:id/live — the night, on a spectator's phone.
 *
 * This is where the TV's follow-along QR lands. Its whole job is to be worth
 * the scan: the boards as they stand, and the table. NOT the bracket, not the
 * fixtures, not the held-visit review — those exist elsewhere for people who
 * need them, and a phone held one-handed at the bar is not the place to put
 * everything.
 *
 * NO SESSION, BY DESIGN. `type=live` is public in live.js ("a spectator view
 * nobody can open is not a spectator view") and read-only at the room, which
 * ignores scoring events from any connection that is not an admin or a paired
 * scorer. Nothing on this page is clickable for that reason.
 *
 * THE SAME SNAPSHOT THE WALL DRAWS. Boards and standings both come off the one
 * room snapshot rather than from REST, so a phone and the TV in the same room
 * cannot disagree about a score — which is exactly the failure a spectator
 * would notice first, standing under the screen.
 *
 * THE TOURNAMENT ID IS IN THE URL HERE, unlike the TV, which hunts for tonight
 * by itself. A QR is minted against a specific night and a link that is shared
 * on should keep meaning that night, not "whatever is on now".
 *
 * Note the route param is `:id`, not `:tournamentId` — see routes.jsx.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiGet } from '../lib/api.js';
import { useTournamentRoom, throwingEntryId, matchAverages } from '../lib/tournamentRoom.js';
import { tvToggles } from '../components/tv/TvAds.jsx';
import { formatAvg, formatDate } from '../lib/format.js';

/* ------------------------------------------------------------------------- */

/** One player's line on a board card. */
function LivePlayer({ name, legs, remaining, throwing, average }) {
  return (
    <div className={`hd-live-player ${throwing ? 'hd-live-throwing' : ''}`}>
      <span className="hd-live-name">
        {/* The marker for whose throw it is has to survive being read at a
            glance in a noisy room, so it is a glyph rather than a colour. */}
        {throwing && <span aria-hidden="true">▸ </span>}
        {name ?? 'TBC'}
      </span>

      <span className="hd-live-legs">{legs ?? 0}</span>

      <span className="hd-live-remaining">
        {remaining != null ? remaining : '—'}
      </span>

      {average != null && <span className="hd-live-avg">{formatAvg(average)}</span>}
    </div>
  );
}

function LiveBoard({ board, showAverages }) {
  const match  = board.match ?? null;
  const isLive = match?.status === 'live';

  // Averages follow the venue's own toggle, the same one the wall reads, so a
  // phone and the TV in one room do not disagree about whether mid-match
  // averages are a thing here. After a match they always show.
  const averages = match && (showAverages || !isLive) ? matchAverages(match) : {};
  const throwing = throwingEntryId(match);
  const legsWon  = match?.legsWon ?? {};

  const currentLeg = isLive
    ? (match.legs ?? []).find((l) => l.id === match.currentLegId) ?? null
    : null;

  const remaining = currentLeg?.remaining ?? {};

  return (
    <div className={`hd-live-board ${isLive ? 'hd-live-board-active' : ''}`}>
      <div className="hd-live-board-head">
        <span className="hd-live-board-number">Board {board.boardNumber}</span>
        {isLive && currentLeg && (
          <span className="hd-meta">
            Leg {currentLeg.leg_number ?? '—'} · first to {match.legsToWin}
          </span>
        )}
      </div>

      {match ? (
        <>
          {[match.player1_id, match.player2_id].map((entryId, i) => (
            <LivePlayer
              key={entryId ?? `slot-${i}`}
              name={i === 0 ? match.player1_name : match.player2_name}
              legs={legsWon[entryId]}
              remaining={remaining[entryId]}
              throwing={throwing != null && throwing === entryId}
              average={averages[entryId]}
            />
          ))}
        </>
      ) : (
        <p className="hd-muted">Waiting for the next match.</p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- */

function LiveStandings({ groups }) {
  if (!groups.length) return null;

  return (
    <div className="hd-stack">
      {groups.map((group) => (
        <div key={group.groupNumber} className="hd-card">
          <h2 className="hd-section-title">
            Group {group.groupNumber}
            {group.boardNumber != null && (
              <span className="hd-meta"> · board {group.boardNumber}</span>
            )}
          </h2>

          <table className="hd-table">
            <thead>
              <tr>
                <th scope="col">Player</th>
                <th scope="col">P</th>
                <th scope="col">W</th>
                <th scope="col">Legs</th>
              </tr>
            </thead>
            <tbody>
              {group.standings.map((row) => (
                <tr key={row.entry_id}>
                  <td>{row.display_name}</td>
                  <td>{row.matches_played}</td>
                  <td>{row.matches_won}</td>
                  <td>{row.legs_won}–{row.legs_lost}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------------- */

export default function PublicLive() {
  const { venueSlug, id: tournamentId } = useParams();

  const [venue, setVenue]       = useState(null);
  const [settings, setSettings] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const { data } = await apiGet(`/api/venues/by-slug/${venueSlug}`);
      if (cancelled) return;

      if (!data?.venue) { setNotFound(true); return; }
      setVenue(data.venue);

      const pub = await apiGet(`/api/venues/${data.venue.id}/settings/public`);
      if (!cancelled && pub.data?.settings) setSettings(pub.data.settings);
    })();

    return () => { cancelled = true; };
  }, [venueSlug]);

  const { status, snapshot } = useTournamentRoom(venue?.id ?? null, tournamentId, 'live');

  if (notFound) {
    return (
      <div className="hd-stack">
        <h1 className="hd-page-title">Live</h1>
        <p className="hd-muted">That venue could not be found.</p>
      </div>
    );
  }

  const { liveAverages } = tvToggles(settings);

  const phase   = snapshot?.phase ?? null;
  const boards  = snapshot?.boards ?? [];
  const groups  = snapshot?.standings ?? [];
  const tournament = snapshot?.tournament ?? null;

  return (
    <div className="hd-stack">
      <div>
        <h1 className="hd-page-title">Live</h1>
        <p className="hd-meta">
          {venue?.name ?? venueSlug}
          {tournament?.night_number != null && <> · Night {tournament.night_number}</>}
          {tournament?.date && <> · {formatDate(tournament.date)}</>}
        </p>
      </div>

      {/* Said once, quietly, and only when it is true. A spectator who has just
          scanned a code needs to know the page is still filling rather than
          empty — but a reconnect banner on every wifi hiccup is noise. */}
      {status !== 'connected' && snapshot == null && (
        <p className="hd-muted">Connecting to the scores…</p>
      )}

      {snapshot && phase === 'predraw' && (
        <p className="hd-muted">
          The draw has not run yet. Scores appear here as soon as it does.
        </p>
      )}

      {snapshot && phase === 'complete' && (
        <p className="hd-muted">That is the night finished. Thanks for coming.</p>
      )}

      {boards.length > 0 && (
        <div className="hd-live-boards">
          {boards.map((board) => (
            <LiveBoard
              key={board.boardNumber}
              board={board}
              showAverages={liveAverages}
            />
          ))}
        </div>
      )}

      {/* Group tables only. A knockout night has a bracket and this is not the
          place for one — the wall has it, and a bracket on a phone is a
          horizontal scroll nobody wins. */}
      {phase === 'group' && <LiveStandings groups={groups} />}
    </div>
  );
}
